import type { ReactNode } from 'react'
import { Auth0Provider, type AppState } from '@auth0/auth0-react'
import { useNavigate } from 'react-router'
import { auth0, authEnabled } from './config'

/**
 * Sign-in with Auth0 (Universal Login: the tenant's own sign-in page). After signing in, Auth0
 * returns to the site's root with a code; the SDK exchanges it and we go back to the page
 * the user was on (`appState.returnTo`).
 *
 * Tokens are kept in localStorage with rotating refresh tokens, so a reload keeps the session
 * without the third-party cookies Safari and Firefox block. The SDK only calls Auth0 on load
 * when this browser has signed in before, so visitors who never sign in make no Auth0 requests.
 */
export function AuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  const navigate = useNavigate()
  if (!authEnabled) return children

  return (
    <Auth0Provider
      domain={auth0.domain!}
      clientId={auth0.clientId!}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: auth0.audience,
        scope: 'openid profile email',
      }}
      cacheLocation="localstorage"
      useRefreshTokens
      onRedirectCallback={(appState?: AppState) =>
        navigate(appState?.returnTo ?? '/', { replace: true })
      }
    >
      {children}
    </Auth0Provider>
  )
}
